import { type Identifier, type JSXIdentifier } from "@babel/types";

import {
	type JsxParam,
	type JsxSimpleObjectArrayParam,
	type JsxStringUnionParam,
} from "~/runtime/cell-compiler/analysis/JsxParameterInfo";
import { standardParserPlugins } from "~/runtime/cell-compiler/parser/standardParserPlugins";
import { refVisitor } from "~/runtime/cell-compiler/visitor/refVisitor";
import { parseAsync, traverseAsync } from "~/utils/deferred-babel";

import { type Snippet } from "./InspectFieldCodeSnippetHandle";

/**
 * Parses JavaScript expression into a `Snippet` object, containing everything we need to execute
 * the snippet.
 */
export const snippetFromExpression = async (
	id: string,
	fieldType: JsxStringUnionParam | JsxSimpleObjectArrayParam | JsxParam,
	expression: string
): Promise<Snippet> => {
	const refs = new Map<string, Identifier | JSXIdentifier>();

	let staticError: Error | undefined;
	try {
		const tree = await parseAsync(`return ${expression}`, {
			parserOpts: { allowReturnOutsideFunction: true },
			plugins: await standardParserPlugins(),
		});

		// NOTE: References must be *unique*. Key them by name to enforce this invariant.
		const refCollector = refVisitor((path) => refs.set(path.node.name, path.node));
		await traverseAsync(tree, refCollector);
	} catch (e) {
		// No refs in case of compiler error.
		staticError = e as Error;
	}

	const refNames = [...refs.values()].map(({ name }) => name);
	const snippet: Snippet = {
		fieldType,
		id,
		expression,
		emittedCode: `(${refNames.join(", ")}) => { return ${expression} }`,
		refs: refNames,
		staticError,
	};
	return snippet;
};
